import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router, RouterModule } from '@angular/router';
import { environment } from '../../../environments/environment';
import { CREDIT_PACKAGES } from '../../config/pricing.config';
import { SeoService } from '../../services/seo.service';

interface CreditPackage {
  credits: number;
  price: number;
  original_price?: number;
  badge?: string;
  popular?: boolean;
}

interface UserCreditsResponse {
  success: boolean;
  data: {
    user?: {
      credits: number;
      totalLinks?: number;
    };
  };
}

interface Faq {
  question: string;
  answer: string;
  open: boolean;
}

@Component({
  selector: 'app-credits',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './credits.html',
  styleUrls: ['./credits.scss']
})
export class CreditsComponent implements OnInit, OnDestroy {
  userCredits = 0;
  packages = CREDIT_PACKAGES;
  isLoggedIn = false;

  // Opportunity Cost Calculator State
  calcUnindexedLinks = 100;
  calcValuePerLink = 50; // Estimated value per backlink in INR or USD
  
  constructor(private http: HttpClient, private router: Router, private seoService: SeoService) { }

  ngOnInit() {
    this.checkAuthStatus();
    this.setFaqSchema();
  }

  ngOnDestroy() {
    this.seoService.removeJsonLd('pricing-faq-schema');
  }

  get opportunityCost(): number {
    // 90% of them not being indexed = lost value
    const missedValue = (this.calcUnindexedLinks * 0.9) * this.calcValuePerLink;
    return Math.floor(missedValue);
  }

  private setFaqSchema() {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': this.pricingFaqs.map(faq => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
        }
      }))
    };
    this.seoService.setJsonLd(faqSchema, 'pricing-faq-schema');
  }

  private checkAuthStatus() {
    const token = localStorage.getItem('auth_token');
    this.isLoggedIn = !!token;

    if (this.isLoggedIn) {
      this.loadUserCredits();
    }
  }

  loadUserCredits() {
    const user = JSON.parse(localStorage.getItem('userData') || '{}');
    this.userCredits = user.credits || 0;

    const token = localStorage.getItem('auth_token');
    if (token) {
      this.http.get<UserCreditsResponse>(`${environment.apiUrl}/user/links-simple.php?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).subscribe({
        next: (response) => {
          if (response.success && response.data && response.data.user) {
            this.userCredits = response.data.user.credits;
            const userData = { ...user, ...response.data.user };
            localStorage.setItem('userData', JSON.stringify(userData));
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to refresh credits:', err);
        }
      });
    }
  }

  goToPayment(selectedPackage: CreditPackage) {
    if (!this.isLoggedIn) {
      this.router.navigate(['/login'], { queryParams: { register: 'true', redirect: 'pricing' } });
      return;
    }

    const packageData = {
      credits: selectedPackage.credits,
      price: selectedPackage.price,
      name: `${selectedPackage.credits} Credits Package`
    };

    this.router.navigate(['/payment'], {
      queryParams: { package: JSON.stringify(packageData) }
    });
  }

  getDiscount(pkg: CreditPackage): number {
    if (!pkg.original_price) return 0;
    return Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100);
  }

  getPerCredit(pkg: CreditPackage): string {
    if (typeof pkg.price !== 'number' || typeof pkg.credits !== 'number') {
      return '';
    }
    return (pkg.price / pkg.credits).toFixed(2);
  }

  getPricePerCredit(pkg: CreditPackage): string {
    return this.getPerCredit(pkg);
  }

  selectPackage(pkg: CreditPackage): void {
    this.goToPayment(pkg);
  }

  // Pricing FAQs
  pricingFaqs: Faq[] = [
    {
      question: 'How many credits do I need?',
      answer: 'Each URL submission costs 1 credit. Choose a package based on how many URLs you want to index. For example, 100 credits = 100 URLs indexed.',
      open: false
    },
    {
      question: 'Do credits expire?',
      answer: 'No, your credits never expire! Use them whenever you need. Once purchased, they remain in your account until used.',
      open: false
    },
    {
      question: 'What payment methods are accepted?',
      answer: 'We accept BTC, ETH, USDT, USDC, and 200+ other cryptocurrencies through NOWPayments. For custom payment options like PayPal, please contact our support.',
      open: false
    },
    {
      question: 'Is there a refund policy?',
      answer: 'Yes, we offer refunds for unused credits within 7 days of purchase. Contact our support team for refund requests.',
      open: false
    }
  ];

  toggleFaq(faq: Faq) {
    faq.open = !faq.open;
  }
}